import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FiRefreshCw, FiArrowUp, FiArrowDown, FiChevronDown } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { RetentionData } from '../../api/types';

const RETENTION_COUNTRY_RADIO_GROUP = 'dashboard-retention-country';

interface RetentionTableProps {
  retentionData: RetentionData[];
  retentionCountries: string[];
  countries: string[];
  loading: boolean;
  /** Ülke / tam yenileme sırasında dropdown ve Yenile (popup Dashboard’da). */
  actionsDisabled?: boolean;
  isRefreshing: boolean;
  onRetentionCountriesChange: (next: string[] | ((prev: string[]) => string[])) => void;
  onRefresh: () => void;
}

const RetentionTable: React.FC<RetentionTableProps> = ({
  retentionData,
  retentionCountries,
  countries,
  loading,
  actionsDisabled = false,
  isRefreshing,
  onRetentionCountriesChange,
  onRefresh,
}) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [countryPanelOpen, setCountryPanelOpen] = useState(false);
  const countryPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!countryPanelOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (countryPanelRef.current && !countryPanelRef.current.contains(e.target as Node)) {
        setCountryPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [countryPanelOpen]);

  const selectedCountry = retentionCountries[0] ?? 'Tüm Ülkeler';
  const countryButtonLabel = selectedCountry;

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Handle sort
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Separate Average row from date rows
  const averageRow = retentionData.find((row) => row.date === 'Average');
  const dateRows = retentionData.filter((row) => row.date !== 'Average');
  
  // Sort by date (newest first) and take last 30 days of data
  const sortedRetentionData = useMemo(() => {
    let sorted = [...dateRows].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Apply user sorting if active
    if (sortColumn === 'date') {
      sorted = [...dateRows].sort((a, b) => {
        const aValue = new Date(a.date).getTime();
        const bValue = new Date(b.date).getTime();
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return sorted.slice(0, 30).reverse();
  }, [dateRows, sortColumn, sortDirection]);
  
  const last30DaysRetention = sortedRetentionData;
  
  const transformedRetentionData = last30DaysRetention.map((row) => ({
    date: row.date,
    day1: row.day_1,
    day2: row.day_2,
    day3: row.day_3,
    day4: row.day_4,
    day5: row.day_5,
    day6: row.day_6,
    day7: row.day_7,
    day8: row.day_8,
    day9: row.day_9,
    day10: row.day_10,
    day11: row.day_11,
    day12: row.day_12,
    day13: row.day_13,
    day14: row.day_14,
    day15: row.day_15,
    day16: row.day_16,
    day17: row.day_17,
    day18: row.day_18,
    day19: row.day_19,
    day20: row.day_20,
    day21: row.day_21,
    day22: row.day_22,
    day23: row.day_23,
    day24: row.day_24,
    day25: row.day_25,
    day26: row.day_26,
    day27: row.day_27,
    day28: row.day_28,
    day29: row.day_29,
    day30: row.day_30,
  }));

  const getRetentionColor = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'bg-white dark:bg-gray-800';
    if (value === 0) return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
    if (value >= 15) return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
    if (value >= 5) return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
    return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
  };

  /** AVG sütunu (satır toplamı): 0–60 kırmızı, 60–100 sarı, 100 üzeri yeşil */
  const getAvgColumnColor = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'bg-white dark:bg-gray-800';
    if (value < 60) return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
    if (value <= 100) return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
    return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
  };

  const calculateAverage = (dayKey: keyof typeof transformedRetentionData[0]): number => {
    const validValues = transformedRetentionData
      .map(row => row[dayKey])
      .filter((value): value is number => value !== null && value !== undefined);
    
    if (validValues.length === 0) return 0;
    return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
  };

  const calculateRowTotal = (row: Record<string, unknown>): number | null => {
    const validValues = Array.from({ length: 30 }, (_, i) => row[`day${i + 1}`])
      .filter((value): value is number => typeof value === 'number');

    if (validValues.length === 0) return null;
    return validValues.reduce((sum, val) => sum + val, 0);
  };

  const calculateAverageRowTotal = (row: RetentionData): number | null => {
    const validValues = Array.from({ length: 30 }, (_, i) => row[`day_${i + 1}` as keyof RetentionData])
      .filter((value): value is number => typeof value === 'number');

    if (validValues.length === 0) return null;
    return validValues.reduce((sum, val) => sum + val, 0);
  };

  const averageDay1 = calculateAverage('day1');
  const averageDay7 = calculateAverage('day7');
  const averageDay14 = calculateAverage('day14');

  return (
    <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex flex-col gap-2 mb-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Retention Tablosu</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
            Ülke seçerek retention ve uygulama metrikleri panosunu o bölgeye göre filtreleyebilirsiniz.
            &quot;Tüm Ülkeler&quot; agregasyonu backend tarafından hesaplanır.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="relative" ref={countryPanelRef}>
            <button
              type="button"
              disabled={actionsDisabled}
              onClick={() => setCountryPanelOpen((o) => !o)}
              className="appearance-none px-4 py-2.5 pr-10 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 hover:border-indigo-300 dark:hover:border-indigo-500 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md min-w-[200px] text-left disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-2"
              aria-expanded={countryPanelOpen}
              aria-haspopup="dialog"
            >
              <span className="truncate">{countryButtonLabel}</span>
              {renderIcon(FiChevronDown as IconType, `w-4 h-4 shrink-0 text-slate-400 ${countryPanelOpen ? 'rotate-180' : ''} transition-transform`)}
            </button>
            {countryPanelOpen && (
              <div
                className="absolute right-0 z-20 mt-2 w-[min(100vw-2rem,320px)] max-h-72 overflow-y-auto rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-lg py-2"
              >
                <p className="px-3 pb-2 text-[11px] text-slate-500 dark:text-slate-400 border-b border-gray-100 dark:border-gray-600">
                  Tek ülke seçin; uygulama metrikleri panosu aynı filtreyi kullanır.
                </p>
                <ul className="py-1" role="radiogroup" aria-label="Retention ülke seçimi">
                  {countries.map((country) => {
                    const checked = selectedCountry === country;
                    const disabledOption = actionsDisabled;
                    return (
                      <li key={country}>
                        <label
                          className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600/80 ${
                            disabledOption ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <input
                            type="radio"
                            name={RETENTION_COUNTRY_RADIO_GROUP}
                            className="border-gray-300 dark:border-gray-500 text-indigo-600 focus:ring-indigo-500"
                            checked={checked}
                            disabled={disabledOption}
                            onChange={() => {
                              if (disabledOption) return;
                              onRetentionCountriesChange([country]);
                            }}
                          />
                          <span className="text-slate-700 dark:text-slate-200">{country}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          <button
            onClick={onRefresh}
            disabled={isRefreshing || loading || actionsDisabled}
            className="px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-700 dark:from-indigo-500 dark:to-indigo-600 text-white rounded-xl hover:from-indigo-700 hover:to-indigo-800 dark:hover:from-indigo-600 dark:hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-semibold shadow-sm hover:shadow-md active:scale-95"
          >
            {renderIcon(FiRefreshCw as IconType, `w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`)}
            Yenile
          </button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">ORTALAMA 1. GÜN GERÇEKLEŞME</p>
          <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{averageDay1.toFixed(2)}%</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">ORTALAMA 7. GÜN GERÇEKLEŞME</p>
          <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{averageDay7.toFixed(2)}%</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">ORTALAMA 14. GÜN GERÇEKLEŞME</p>
          <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{averageDay14.toFixed(2)}%</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">TOPLAM KAYIT</p>
          <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{transformedRetentionData.length}</p>
        </div>
      </div>

      {/* Retention Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th 
                className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 sticky left-0 bg-gray-50 dark:bg-gray-700 z-10 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                onClick={() => handleSort('date')}
              >
                <div className="flex items-center gap-2">
                  <span>DATE</span>
                  {sortColumn === 'date' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                </div>
              </th>
              <th className="px-2 py-2 text-center font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 min-w-[80px]">
                AVG
              </th>
              {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => (
                <th key={day} className="px-2 py-2 text-center font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 min-w-[70px]">
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {transformedRetentionData.length === 0 ? (
              <tr>
                <td colSpan={32} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                  {loading ? 'Yükleniyor...' : 'Retention verisi bulunamadı'}
                </td>
              </tr>
            ) : (
              <>
                {transformedRetentionData.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 py-2 font-medium text-slate-700 dark:text-slate-300 border border-gray-200 dark:border-gray-600 sticky left-0 bg-white dark:bg-gray-800 z-10 whitespace-nowrap">
                      {row.date}
                    </td>
                    {(() => {
                      const rowTotal = calculateRowTotal(row as Record<string, unknown>);
                      const isEmpty = rowTotal === null;
                      return (
                        <td
                          className={`px-2 py-2 text-center border border-gray-200 dark:border-gray-600 font-semibold ${getAvgColumnColor(rowTotal ?? undefined)}`}
                        >
                          {isEmpty ? '' : `${rowTotal.toFixed(2)}%`}
                        </td>
                      );
                    })()}
                    {Array.from({ length: 30 }, (_, i) => {
                      const dayKey = `day${i + 1}` as keyof typeof row;
                      const value = row[dayKey] as number | null | undefined;
                      const isEmpty = value === null || value === undefined;
                      return (
                        <td
                          key={i}
                          className={`px-2 py-2 text-center border border-gray-200 dark:border-gray-600 ${getRetentionColor(value)}`}
                        >
                          {isEmpty ? '' : `${value.toFixed(2)}%`}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {averageRow && (
                  <tr className="bg-gray-100 dark:bg-gray-700 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
                    <td className="px-3 py-2 font-bold text-slate-800 dark:text-white border border-gray-200 dark:border-gray-600 sticky left-0 bg-gray-100 dark:bg-gray-700 z-10 whitespace-nowrap">
                      {averageRow.date}
                    </td>
                    {(() => {
                      const totalOfAverageRow = calculateAverageRowTotal(averageRow);
                      const isEmpty = totalOfAverageRow === null;
                      return (
                        <td
                          className={`px-2 py-2 text-center border border-gray-200 dark:border-gray-600 font-bold ${getAvgColumnColor(totalOfAverageRow ?? undefined)}`}
                        >
                          {isEmpty ? '' : `${totalOfAverageRow.toFixed(2)}%`}
                        </td>
                      );
                    })()}
                    {Array.from({ length: 30 }, (_, i) => {
                      const dayKey = `day_${i + 1}` as keyof typeof averageRow;
                      const value = averageRow[dayKey] as number | null | undefined;
                      const isEmpty = value === null || value === undefined;
                      return (
                        <td
                          key={i}
                          className={`px-2 py-2 text-center border border-gray-200 dark:border-gray-600 font-semibold ${getRetentionColor(value)}`}
                        >
                          {isEmpty ? '' : `${value.toFixed(2)}%`}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RetentionTable;

